// src/pages/lab/processing/components/StartAnalysisProtocolModal.tsx
import React, { useState, useEffect } from "react";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import { Button, Badge, Radio, Select, Input, Tabs, Collapse, Space, Typography, Row, Col, Alert, Divider, message } from "antd";
import { CloseOutlined, PlayCircleOutlined, ExclamationCircleOutlined, CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

// Shared components
import { DataTable, SectionCard, DetailDrawer } from "../../../../../components/shared";

// API
import { createProcessingGroup } from "../../../../../api/labiq/GroupProcessingApi";
import { fetchTeamMembers } from "../../../../../api/admin/teamApi";

// Types
import { SampleAnalysis } from "../../../../../types/lab/SampleTypes";
import { Protocol } from "../../../../../types/lab/ProtocolTypes";
import { User } from "../../../../../types/admin/UserTypes";

// Hooks
import { useNotifications } from "../../../../../context/NotificationContext";
import { TeamMember } from "../../../../../types/admin/TeamTypes";
import { hasAnyRoleInGroup } from "../../../../../utils/userPrefs/validatePermissions";
import { requireLabiqUserId } from "../../../../../utils/auth/labiqUserId";
import type { LegacyUserId } from "../../../../../types/identity";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface StartAnalysisProtocolModalProps {
	isOpen: boolean;
	analyses: SampleAnalysis[];
	protocols: Protocol[];
	onClose: () => void;
	onProtocolStarted: () => void;
}

const StartAnalysisProtocolModal: React.FC<StartAnalysisProtocolModalProps> = ({ isOpen, analyses, protocols, onClose, onProtocolStarted }) => {
	const user: User = getSessionUser();

	const [compatibleProtocols, setCompatibleProtocols] = useState<Protocol[]>([]);
	const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);
	const [assignedTo, setAssignedTo] = useState<LegacyUserId | undefined>(undefined);
	const [notes, setNotes] = useState<string>("");
	const [loading, setLoading] = useState(true);
	const [starting, setStarting] = useState(false);
	const [activeTab, setActiveTab] = useState("1");
	const [availableTechs, setAvailableTechs] = useState<TeamMember[]>([]);

	const [groupName, setGroupName] = useState<string>("");
	const [groupDescription, setGroupDescription] = useState<string>("");

	const { showSuccess, showError, showWarning } = useNotifications();

	useEffect(() => {
		if (isOpen) {
			loadCompatibleProtocols();
			loadTechnicians();

			if (!groupName) {
				const timestamp = new Date().toISOString().slice(0, 10);
				const analysisIds = analyses
					.slice(0, 3)
					.map((a) => a.analysis_id)
					.join(", ");
				const suffix = analyses.length > 3 ? ` +${analyses.length - 3} more` : "";
				setGroupName(`Analysis Group ${timestamp} - ${analysisIds}${suffix}`);
			}
		}
	}, [isOpen, analyses]);

	const loadCompatibleProtocols = async () => {
		setLoading(true);
		try {
			// Filter for NON-first protocols only
			const allCompatibleProtocols = protocols.filter((p) => p.is_active && !p.is_first_protocol);
			setCompatibleProtocols(allCompatibleProtocols);
		} catch (error) {
			showError("Failed to load compatible protocols");
			setCompatibleProtocols(protocols.filter((p) => p.is_active && !p.is_first_protocol));
		} finally {
			setLoading(false);
		}
	};

	const loadTechnicians = async () => {
		try {
			let { data } = await fetchTeamMembers(1, 100);
			let techOptions: TeamMember[] = [];
			for (const tech of data) {
				if (hasAnyRoleInGroup("LAB", tech)) {
					techOptions.push(tech);
				}
			}
			setAvailableTechs(techOptions);
		} catch (error) {
			showError("Failed to load technicians");
		}
	};

	const handleCreateProtocolGroup = async () => {
		if (!selectedProtocol) {
			message.warning("Please select a protocol to start");
			return;
		}

		if (!groupName.trim()) {
			showWarning("Please enter a group name");
			return;
		}

		setStarting(true);
		try {
			const group = await createProcessingGroup({
				group_name: groupName,
				description: groupDescription || undefined,
				protocol_id: selectedProtocol.id,
				analysis_ids: analyses.map((a) => a.analysis_id),
				created_by: requireLabiqUserId(user),
			});

			showSuccess(`Group processing created for "${groupName}" with ${analyses.length} analyses`);

			onProtocolStarted();
		} catch (error) {
			showError("Failed to start protocol");
			console.error("Protocol start error:", error);
		} finally {
			setStarting(false);
		}
	};

	const analyzeProtocol = (protocol: Protocol) => {
		const steps = protocol.steps || [];

		const totalDuration = steps.reduce((sum, step) => sum + step.duration_estimate, 0);
		const totalMaterials = steps.reduce((sum, step) => sum + (step.materials?.length || 0), 0);
		const stepsWithTimeout = steps.filter((step) => step.timeout_minutes).length;
		const stepsWithConditions = steps.filter((step) => step.completion_conditions).length;
		const totalActions = steps.reduce((sum, step) => sum + (step.completion_actions?.length || 0) + (step.failure_actions?.length || 0) + (step.timeout_actions?.length || 0), 0);

		return {
			totalDuration,
			totalMaterials,
			stepsWithTimeout,
			stepsWithConditions,
			totalActions,
		};
	};

	const protocolColumns: ColumnsType<Protocol> = [
		{
			title: "Select",
			key: "select",
			width: 60,
			render: (_: any, record: Protocol) => <Radio checked={selectedProtocol?.id === record.id} onChange={() => setSelectedProtocol(record)} />,
		},
		{
			title: "Protocol",
			key: "protocol",
			render: (_: any, record: Protocol) => {
				const analysis = analyzeProtocol(record);

				return (
					<div>
						<div style={{ marginBottom: "8px" }}>
							<Text strong style={{ fontSize: "13px" }}>
								{record.name}
							</Text>
							<Text type="secondary" style={{ fontSize: "11px", marginLeft: "8px" }}>
								v{record.version}
							</Text>
						</div>

						<Space wrap size={4} style={{ marginBottom: "8px" }}>
							<Badge count={`${record.steps?.length || 0} steps`} style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />
							{analysis.totalMaterials > 0 && <Badge count={`${analysis.totalMaterials} materials`} style={{ backgroundColor: "var(--mg-warning)", fontSize: "10px" }} />}
							{analysis.totalActions > 0 && <Badge count={`${analysis.totalActions} auto-actions`} style={{ backgroundColor: "var(--mg-success)", fontSize: "10px" }} />}
							{analysis.stepsWithTimeout > 0 && <Badge count={`${analysis.stepsWithTimeout} timeouts`} style={{ backgroundColor: "var(--mg-danger)", fontSize: "10px" }} />}
						</Space>

						{record.description && (
							<Text type="secondary" style={{ fontSize: "12px", display: "block" }}>
								{record.description.length > 150 ? `${record.description.slice(0, 150)}...` : record.description}
							</Text>
						)}
					</div>
				);
			},
		},
		{
			title: "Duration",
			key: "duration",
			width: 100,
			render: (_: any, record: Protocol) => {
				const analysis = analyzeProtocol(record);
				const hours = Math.floor(analysis.totalDuration / 60);
				const minutes = analysis.totalDuration % 60;

				return <Text style={{ fontSize: "12px" }}>{hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`}</Text>;
			},
		},
	];

	const renderAnalysisSummary = () => {
		if (analyses.length === 0) return null;

		const uniqueProtocols = [...new Set(analyses.map((a) => a.protocol_name))];
		const uniqueClients = [...new Set(analyses.map((a) => a.client))];

		return (
			<SectionCard title={`Group Processing: ${analyses.length} Analyses`}>
				<Row gutter={[16, 16]}>
					<Col span={12}>
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<div style={{ display: "flex", justifyContent: "space-between" }}>
								<Text type="secondary" style={{ fontSize: "12px" }}>
									Total Analyses:
								</Text>
								<Badge count={analyses.length} style={{ backgroundColor: "var(--mg-primary)" }} />
							</div>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								<Text type="secondary" style={{ fontSize: "12px" }}>
									Clients:
								</Text>
								<Space wrap size={4}>
									{uniqueClients.slice(0, 3).map((client, index) => (
										<Badge key={index} count={client} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />
									))}
									{uniqueClients.length > 3 && <Badge count={`+${uniqueClients.length - 3}`} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />}
								</Space>
							</div>
						</Space>
					</Col>
					<Col span={12}>
						<Space direction="vertical" size="small" style={{ width: "100%" }}>
							<Text type="secondary" style={{ fontSize: "12px" }}>
								Analysis Types:
							</Text>
							<Space wrap size={4}>
								{uniqueProtocols.length > 0 ? (
									uniqueProtocols.map((protocol, index) => <Badge key={index} count={protocol} style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />)
								) : (
									<Text type="secondary" style={{ fontSize: "12px" }}>
										None specified
									</Text>
								)}
							</Space>

							<div style={{ marginTop: "8px" }}>
								<Text type="secondary" style={{ fontSize: "12px" }}>
									Analysis IDs:
								</Text>
								<Text type="secondary" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
									{analyses
										.slice(0, 5)
										.map((a) => a.analysis_id)
										.join(", ")}
									{analyses.length > 5 && ` +${analyses.length - 5} more`}
								</Text>
							</div>
						</Space>
					</Col>
				</Row>
			</SectionCard>
		);
	};

	const renderProtocolPreview = () => {
		if (!selectedProtocol) return null;

		const analysis = analyzeProtocol(selectedProtocol);
		const steps = selectedProtocol.steps || [];

		return (
			<SectionCard>
				<Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
					<Col span={12}>
						<div style={{ fontSize: "13px" }}>
							<Text strong>Workflow Overview:</Text>
							<Space direction="vertical" size="small" style={{ width: "100%", marginTop: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Total Duration:</Text>
									<Text strong>
										{Math.floor(analysis.totalDuration / 60)}h {analysis.totalDuration % 60}m
									</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Materials Needed:</Text>
									<Text strong>{analysis.totalMaterials}</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Automation Actions:</Text>
									<Text strong>{analysis.totalActions}</Text>
								</div>
							</Space>
						</div>
					</Col>
					<Col span={12}>
						<div style={{ fontSize: "13px" }}>
							<Text strong>Advanced Features:</Text>
							<Space direction="vertical" size="small" style={{ width: "100%", marginTop: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Steps with Conditions:</Text>
									<Text strong>{analysis.stepsWithConditions}</Text>
								</div>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text type="secondary">Steps with Timeouts:</Text>
									<Text strong>{analysis.stepsWithTimeout}</Text>
								</div>
							</Space>
						</div>
					</Col>
				</Row>

				<Collapse
					ghost
					items={[
						{
							key: "1",
							label: (
								<Space>
									<span>Step-by-Step Workflow ({steps.length} steps)</span>
								</Space>
							),
							children: (
								<Space direction="vertical" size="small" style={{ width: "100%" }}>
									{steps.map((step) => (
										<SectionCard key={step.id} style={{ border: "1px solid var(--mg-border)" }}>
											<div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
												<Badge count={step.step_number} style={{ backgroundColor: "var(--mg-primary)" }} />
												<div style={{ flex: 1 }}>
													<Space style={{ marginBottom: "4px" }}>
														<Text strong style={{ fontSize: "13px" }}>
															{step.name}
														</Text>
														<Badge count={step.step_type} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />
														<Badge count={`${Math.floor(step.duration_estimate / 60)}h ${step.duration_estimate % 60}m`} style={{ backgroundColor: "var(--mg-text-secondary)", fontSize: "10px" }} />
													</Space>

													{step.description && (
														<Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
															{step.description.length > 100 ? `${step.description.slice(0, 100)}...` : step.description}
														</Text>
													)}

													<Space wrap size={4}>
														{(step.method?.length || 0) > 0 && <Badge count={`${step.method!.length} methods`} style={{ backgroundColor: "var(--mg-success)", fontSize: "10px" }} />}
														{(step.materials?.length || 0) > 0 && <Badge count={`${step.materials!.length} materials`} style={{ backgroundColor: "var(--mg-warning)", fontSize: "10px" }} />}
														{step.completion_conditions && <Badge count="conditions" style={{ backgroundColor: "var(--mg-primary)", fontSize: "10px" }} />}
														{(step.completion_actions?.length || 0) + (step.failure_actions?.length || 0) + (step.timeout_actions?.length || 0) > 0 && (
															<Badge
																count={`${(step.completion_actions?.length || 0) + (step.failure_actions?.length || 0) + (step.timeout_actions?.length || 0)} actions`}
																style={{ backgroundColor: "var(--mg-info)", fontSize: "10px" }}
															/>
														)}
														{step.timeout_minutes && <Badge count={`${step.timeout_minutes}min timeout`} style={{ backgroundColor: "var(--mg-danger)", fontSize: "10px" }} />}
													</Space>
												</div>
											</div>
										</SectionCard>
									))}
								</Space>
							),
						},
					]}
				/>
			</SectionCard>
		);
	};

	const renderConfiguration = () => {
		if (!selectedProtocol) return null;

		// labiq assigns technicians by legacy integer id.
		const technicianOptions = availableTechs
			.filter((tech) => tech.legacy_user_id != null)
			.map((tech) => ({
				label: `${tech.first_name} ${tech.last_name}`,
				value: tech.legacy_user_id,
			}));

		return (
			<SectionCard title="Configuration">
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					<Text type="secondary" style={{ fontSize: "11px", display: "block", marginTop: "-8px", marginLeft: "24px" }}>
						All analyses will be processed together as one group through each protocol step
					</Text>

					<div>
						<Text strong style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
							Group Name *
						</Text>
						<Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Enter group name" size="small" />
					</div>

					<div>
						<Text strong style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
							Group Description (Optional)
						</Text>
						<TextArea value={groupDescription} onChange={(e) => setGroupDescription(e.target.value)} placeholder="Add notes about this processing group" rows={2} size="small" />
					</div>

					<div>
						<Text strong style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
							Assign to Technician
						</Text>
						<Select value={assignedTo} onChange={(value) => setAssignedTo(value)} placeholder="Select technician" style={{ width: "100%" }} size="small" allowClear>
							{technicianOptions.map((tech) => (
								<Option key={tech.value} value={tech.value}>
									{tech.label}
								</Option>
							))}
						</Select>
						<Text type="secondary" style={{ fontSize: "11px", display: "block", marginTop: "4px" }}>
							This technician will be responsible for the entire group
						</Text>
					</div>

					<div>
						<Text strong style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
							Processing Notes (Optional)
						</Text>
						<TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any special notes, priorities, or instructions" rows={3} size="small" />
					</div>
				</Space>

				<Divider style={{ margin: "16px 0" }} />

				<div>
					<Text strong style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
						Readiness Check:
					</Text>
					<Row gutter={[8, 8]}>
						<Col span={12}>
							<Space size={4}>
								<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
								<Text style={{ fontSize: "12px" }}>All analyses ready for processing</Text>
							</Space>
						</Col>
						<Col span={12}>
							<Space size={4}>
								<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
								<Text style={{ fontSize: "12px" }}>Protocol compatibility confirmed</Text>
							</Space>
						</Col>
						<Col span={12}>
							<Space size={4}>
								{assignedTo ? <CheckCircleOutlined style={{ color: "var(--mg-success)" }} /> : <ClockCircleOutlined style={{ color: "var(--mg-border)" }} />}
								<Text style={{ fontSize: "12px" }}>Technician assignment {assignedTo ? "configured" : "optional"}</Text>
							</Space>
						</Col>
						<Col span={12}>
							<Space size={4}>
								<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
								<Text style={{ fontSize: "12px" }}>Ready to start processing</Text>
							</Space>
						</Col>
					</Row>
				</div>
			</SectionCard>
		);
	};

	return (
		<DetailDrawer
			title={`Start Analysis Protocol: ${analyses.length} Analyses`}
			open={isOpen}
			onClose={onClose}
			width="85%"
			headerActions={
				<Space>
					<Button icon={<CloseOutlined />} onClick={onClose} disabled={starting} size="small">
						Cancel
					</Button>
					<Button type="primary" icon={<PlayCircleOutlined />} onClick={handleCreateProtocolGroup} disabled={!selectedProtocol || starting || !groupName.trim()} loading={starting} size="small">
						Create Processing Group
					</Button>
				</Space>
			}
		>
			<Space direction="vertical" size="middle" style={{ width: "100%" }}>
				{renderAnalysisSummary()}

				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={[
						{
							key: "1",
							label: "Available Protocols",
							children: (
								<SectionCard title="Select Processing Protocol">
									<Alert
										message="Analysis Protocols Only"
										description="Only protocols configured for analysis processing are shown. First protocols (sample processing) are not available for analyses."
										type="info"
										showIcon
										style={{ marginBottom: "16px" }}
									/>
									{loading ? (
										<div style={{ textAlign: "center", padding: "32px" }}>
											<Text type="secondary">Loading compatible protocols...</Text>
										</div>
									) : compatibleProtocols.length > 0 ? (
										<div style={{ maxHeight: "400px", overflow: "auto" }}>
											<DataTable
											bare
											columns={protocolColumns}
											dataSource={compatibleProtocols}
											rowKey="id"
											pagination={false}
											onRowClick={(record) => setSelectedProtocol(record)}
										/>
										</div>
									) : (
										<div style={{ textAlign: "center", padding: "32px" }}>
											<ExclamationCircleOutlined style={{ fontSize: "48px", color: "var(--mg-border)", marginBottom: "16px" }} />
											<Title level={5} type="secondary">
												No Analysis Protocols Found
											</Title>
											<Text type="secondary" style={{ fontSize: "12px" }}>
												No active protocols are configured for analysis processing.
											</Text>
										</div>
									)}
								</SectionCard>
							),
						},
						{
							key: "2",
							label: "Protocol Preview",
							disabled: !selectedProtocol,
							children: renderProtocolPreview(),
						},
						{
							key: "3",
							label: "Configuration",
							disabled: !selectedProtocol,
							children: renderConfiguration(),
						},
					]}
				/>

				<Divider style={{ margin: "8px 0" }} />

				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<div>
						{selectedProtocol ? (
							<Space size={4}>
								<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
								<Text type="secondary" style={{ fontSize: "12px" }}>
									Protocol selected: {selectedProtocol.name} v{selectedProtocol.version}• Processing as group: {groupName}
								</Text>
							</Space>
						) : (
							<Space size={4}>
								<ClockCircleOutlined style={{ color: "var(--mg-primary)" }} />
								<Text type="secondary" style={{ fontSize: "12px" }}>
									Select a protocol to continue
								</Text>
							</Space>
						)}
					</div>
				</div>
			</Space>
		</DetailDrawer>
	);
};

export default StartAnalysisProtocolModal;
